import { useEffect, useRef, useState } from "react";
import { Pencil, MapPin, AlertCircle } from "lucide-react";
import type { AddressLookup } from "@shared-kernel/domain/types/address-lookup";
import type { SUPPORTED_COUNTRY } from "@shared-kernel/domain/types/supported-country";
import { Popover, PopoverContent, PopoverTrigger } from "@shared-kernel/primary/shared/shadcn/ui/popover";
import { Button } from "@shared-kernel/primary/shared/shadcn/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@shared-kernel/primary/shared/shadcn/ui/command";
import { Skeleton } from "@shared-kernel/primary/shared/shadcn/ui/skeleton";
import { cn } from "@shared-kernel/primary/shared/shadcn/utils/utils";
import { useAddressSearch } from "./use-address-search";

interface Props {
  value: AddressLookup | null;
  onChange: (value: AddressLookup | null) => void;
  placeholder?: string;
  disabled?: boolean;
  country: SUPPORTED_COUNTRY;
}

export function AddressAutocompleteInput({ value, onChange, placeholder = "Rechercher une adresse...", disabled = false, country }: Props) {
  const [isOpen, setIsOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const { query, setQuery, suggestions, isLoading, sessionToken, resetSessionToken } = useAddressSearch(country);

  useEffect(() => {
    if (isEditing && isOpen && inputRef.current) {
      inputRef.current.select();
    }
  }, [isEditing, isOpen]);

  const hasValue = value !== null;
  const isValid = hasValue && !value.invalid;
  const isInvalid = hasValue && value.invalid;
  const showLocked = (isValid || isInvalid) && !isEditing;

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    if (!open) {
      setIsEditing(false);
      setQuery("");
    }
  };

  const handleSelect = (placeId: string) => {
    const suggestion = suggestions.find(s => s.placeId === placeId);
    if (!suggestion) return;
    onChange({ placeId: suggestion.placeId, description: suggestion.description, sessionToken, invalid: false });
    setIsOpen(false);
    setIsEditing(false);
    setQuery("");
    resetSessionToken();
  };

  const handleEdit = () => {
    if (value) setQuery(value.description);
    setIsEditing(true);
    setIsOpen(true);
  };

  return (
    <Popover open={isOpen} onOpenChange={handleOpenChange} modal>
      {showLocked ? (
        <div
          className={cn(
            "flex h-9 w-full items-center gap-2 rounded-md border px-3 py-2 text-sm",
            isInvalid ? "border-destructive/50 bg-destructive/5" : "border-input bg-background",
          )}
        >
          <MapPin className={cn("size-4 shrink-0", isInvalid ? "text-destructive" : "text-muted-foreground")} />
          <span className={cn("min-w-0 flex-1 truncate", isInvalid && "text-destructive/80 italic")}>
            {value.description || "Adresse invalide"}
          </span>
          {isInvalid && <AlertCircle className="size-4 shrink-0 text-destructive" />}
          {!disabled && (
            <PopoverTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="size-6 shrink-0 text-muted-foreground hover:text-foreground"
                onClick={handleEdit}
                title="Modifier l'adresse"
              >
                <Pencil className="size-3" />
              </Button>
            </PopoverTrigger>
          )}
        </div>
      ) : (
        <PopoverTrigger asChild disabled={disabled}>
          <Button
            type="button"
            variant="outline"
            role="combobox"
            aria-expanded={isOpen}
            className="size-full justify-between overflow-hidden"
          >
            <span className="min-w-0 truncate font-normal opacity-50">{placeholder}</span>
          </Button>
        </PopoverTrigger>
      )}
      <PopoverContent className="w-[400px] p-0" collisionPadding={16} modal>
        <Command shouldFilter={false}>
          <CommandInput ref={inputRef} placeholder="Tapez une adresse..." value={query} onValueChange={setQuery} />
          <CommandList>
            {query.length < 3 ? (
              <div className="flex items-center gap-2 px-4 py-5 text-sm text-muted-foreground">
                <MapPin className="size-4 shrink-0" />
                <span>Saisir au moins 3 caractères pour rechercher</span>
              </div>
            ) : isLoading ? (
              <div className="space-y-2 p-2">
                <Skeleton className="h-8 w-full" />
                <Skeleton className="h-8 w-full" />
                <Skeleton className="h-8 w-full" />
              </div>
            ) : (
              <>
                <CommandEmpty>Aucune adresse trouvée</CommandEmpty>
                <CommandGroup>
                  {suggestions.map(suggestion => (
                    <CommandItem key={suggestion.placeId} value={suggestion.placeId} onSelect={handleSelect}>
                      {suggestion.description}
                    </CommandItem>
                  ))}
                </CommandGroup>
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
